import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

// Color palette - Premium refined
const colors = {
  deepNavy: "#0A2240",
  amber: "#E8A33D",
};

// Kinetic Typography
function KineticText({ children, className = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const words = children.split(" ");

  return (
    <span ref={ref} className={className}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          className="inline-block mr-[0.25em]"
          initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
          animate={isInView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
          transition={{ duration: 0.6, delay: i * 0.04, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {word}
        </motion.span>
      ))}
    </span>
  );
}

// 3D Tilt Card
function TiltCard({ children, className = "" }) {
  const ref = useRef(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);

  const handleMouse = (e) => {
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;
    setRotateX(-y * 8);
    setRotateY(x * 8);
  };

  const reset = () => {
    setRotateX(0);
    setRotateY(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={reset}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

import { useState } from "react";

const whatSetsUsApart = [
  {
    title: "Country Anchored, Globally Enabled",
    desc: "Governments own the agenda and set the priorities. We bring the technical depth and global reach to help them deliver, building system readiness, strengthening clinical leadership, and deploying expertise in service of goals that countries themselves have defined.",
  },
  {
    title: "Singularly Focused on Audacious Goals",
    desc: "A 30% reduction in low birth weight and a 50% reduction in neonatal deaths. Not aspirational talking points, the two outcomes every decision we make is measured against. Every partnership, every dollar, every innovation has to move one of those two numbers.",
  },
  {
    title: "Evidence-Based and Innovation Enabled",
    desc: "Grounded in the best available science, but shaped by the reality of district hospitals where the power cuts out, the oxygen supply is unreliable, and three nurses cover a ward built for one. We focus on innovations ready to perform under real conditions, not ideal ones.",
  },
  {
    title: "Working Across the Continuum",
    desc: "The healthiest births don't begin in the delivery room. They begin with the health of a woman long before she conceives, continue through pregnancy and childbirth, and extend into the critical first 28 days of life. We work across that entire pathway because fragmented interventions produce fragmented results.",
  },
  {
    title: "Sustainability Built Into Design",
    desc: "Public financing sits at the nucleus. Everything we build around it, catalytic investments, blended financing, systems strengthening, is designed to unlock domestic resources, not substitute for them. When the initiative moves on, the systems it built should still be standing.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-16 bg-white">
      {/* Header */}
      <section className="bg-white py-32 md:py-40">
        <div className="max-w-5xl mx-auto px-6">
          <motion.p 
            className="text-[#E8A33D] font-body text-xs tracking-[0.4em] uppercase mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            About the Initiative
          </motion.p>
          <h1 data-testid="about-title" className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#0A2240] leading-[1] mb-10 max-w-5xl tracking-tight">
            <KineticText>A</KineticText>{" "}
            <span className="text-[#E8A33D]"><KineticText>Collective Action Platform</KineticText></span>{" "}
            <KineticText>for Accelerated Reduction of Low Birth Weight & Improved Outcomes for Small and Sick Newborns</KineticText>
          </h1>
        </div>
      </section>

      {/* Why Targets Remain Off Track */}
      <section className="bg-[#fafafa] py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
            <div>
              <motion.p 
                className="text-[#E8A33D] font-body text-xs tracking-[0.4em] uppercase mb-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                Why Targets Remain Off Track
              </motion.p>
              <motion.h2 
                className="font-heading text-4xl md:text-5xl font-bold text-[#0A2240] mb-10 leading-[1.05] tracking-tight"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                Effective interventions are available, but have not translated into population level impact
              </motion.h2>
              <motion.div 
                className="space-y-6 text-slate-500 font-body text-base leading-relaxed"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <p>Technical packages for newborn care are well established; yet 64 countries are off track to meet the SDG neonatal mortality target by 2030.</p>
                <p>Proven innovations demonstrate effectiveness but remain trapped at pilot scale. Technologies that are standard in high-income settings have had limited contextual evidence base from LMICs.</p>
                <p>47% of under-five deaths now occur in the neonatal period, yet newborn-specific donor funding across ODA, philanthropy, and domestic budgets remains a fraction of total child health spending.</p>
              </motion.div>
            </div>
            <div className="space-y-4">
              {[
                { label: "Evidence → Action Gap", text: "The research and clinical guidance to prevent these deaths exists across hundreds of institutions, but remains siloed across disciplines, geographies, and partner initiatives with no single platform consolidating what works into actionable guidance for countries." },
                { label: "Innovation → Scale Gap", text: "Limited pathways exist for regulatory approval, procurement, and integration into national health programs." },
                { label: "Practice → Policy Gap", text: "Governments lack accessible platforms to learn from successful implementation models in other countries. Lessons from country programs are not consistently translated into global guidance or policy frameworks." },
              ].map((item, i) => (
                <motion.div 
                  key={item.label}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                >
                  <TiltCard>
                    <div className="bg-white p-8 hover:shadow-xl transition-all duration-500 rounded-2xl">
                      <p className="text-[#E8A33D] font-body text-xs tracking-[0.2em] uppercase font-medium mb-3">{item.label}</p>
                      <p className="text-[#0A2240]/70 font-body text-sm leading-relaxed">{item.text}</p>
                    </div>
                  </TiltCard>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="bg-white py-32">
        <div className="max-w-6xl mx-auto px-6">
          <motion.p 
            className="text-[#E8A33D] font-body text-xs tracking-[0.4em] uppercase mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            What Sets Us Apart
          </motion.p>
          <motion.h2 
            className="font-heading text-4xl md:text-5xl font-bold text-[#0A2240] mb-20 leading-[1.05] tracking-tight max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Built different. Designed to deliver.
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {whatSetsUsApart.map((item, i) => (
              <motion.div 
                key={item.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <TiltCard className="h-full">
                  <div className="bg-[#fafafa] p-8 h-full group hover:bg-[#0A2240] transition-all duration-700 rounded-2xl">
                    <div className="flex items-start gap-4 mb-4">
                      <motion.div 
                        className="w-10 h-10 bg-[#0A2240] group-hover:bg-white/20 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-700"
                        whileHover={{ rotate: 5 }}
                      >
                        <span className="text-white font-heading font-bold text-sm">{String(i + 1).padStart(2, '0')}</span>
                      </motion.div>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-[#0A2240] group-hover:text-white mb-3 tracking-tight transition-colors duration-700">{item.title}</h3>
                    <p className="text-slate-500 group-hover:text-white/60 font-body text-sm leading-relaxed transition-colors duration-700">{item.desc}</p>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee Statement & CTA */}
      <section className="py-32" style={{ backgroundColor: colors.deepNavy }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.p 
            className="text-white/30 font-body text-xs tracking-[0.4em] uppercase mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            The Path Forward
          </motion.p>
          <motion.h2 
            className="font-heading text-3xl md:text-5xl font-bold text-white mb-10 leading-[1.1] tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            A 30% reduction in low birth weight. A 50% reduction in small and sick newborn mortality. Measurable. Accountable. Achievable.
          </motion.h2>
          <motion.p 
            className="text-white/40 font-body text-base mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Four engines. Evidence-based strategy. A formalized partnership architecture. And the collective will to deliver.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/framework" data-testid="about-cta-framework">
              <motion.button 
                className="inline-flex items-center gap-3 bg-white text-[#0A2240] px-10 py-5 text-base font-body font-medium rounded-full transition-all duration-300 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Our Framework for Action <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
